// client/src/router.js
import { createRouter, createWebHistory } from "vue-router";
import Home from "./views/Home.vue";
import Dashboard from "./views/Dashboard.vue";
import AquariumDetail from "./views/AquariumDetail.vue";
import AquariumDemo from "./views/AquariumDemo.vue";
import About from "./views/About.vue";
import Privacy from "./views/Privacy.vue";
import Terms from "./views/Terms.vue";
import AdminDashboard from "./views/Admin/AdminDashboard.vue";
import UserManagement from "./views/Admin/UserManagement.vue";
import AquariumManagement from "./views/Admin/AquariumManagement.vue";
import LogManagement from "./views/Admin/LogManagement.vue";
import NotFound from "./views/404.vue";
import { useAuthStore } from "@/store";
import { usePopupStore} from "@/store";
import { useModalStore } from "@/store";
import axios from 'axios';
import BlogWhySpreadsheetsFail from "./views/blog/WhySpreadsheetsFail.vue";
import BlogTop5WaysToKeepTankStable from "./views/blog/Top5WaysToKeepTankStable.vue";
import BlogBestReefTankLogAppOptions from "./views/blog/BestReefTankLogAppOptions.vue";
import BlogHowToTrackParameters from "./views/blog/HowToTrackParameters.vue";
import BlogReefTankMaintenanceSchedule from "./views/blog/ReefTankMaintenanceSchedule.vue";
import BlogSpreadsheetsVsApps from "./views/blog/SpreadsheetsVsApps.vue";
import BlogHowSharingLogsHelps from "./views/blog/HowSharingLogsHelps.vue";
import BlogBeginnersGuideToLogging from "./views/blog/BeginnersGuideToLogging.vue";
import BlogTop10MostImportant from "./views/blog/Top10MostImportant.vue";

// Define routes
const routes = [
  // Public Routes (Anyone can access)
  { path: "/", name: "Home", component: Home },
  { path: "/about", name: "About", component: About },
  { path: "/terms", name: "Terms", component: Terms },
  { path: "/privacy", name: "Privacy", component: Privacy },
  { path: "/demo-aquarium", name: "Aquarium Demo", component: AquariumDemo},

  // Authenticated User Routes (Login required)
  { path: "/dashboard", name: "Dashboard", component: Dashboard, meta: { requiresAuth: true } },
  { path: "/aquarium/:id", name: "Aquarium Detail", component: AquariumDetail, meta: { requiresAuth: true } },

  // Admin-Only Routes
  { path: "/admin", name: "Admin Dashboard", component: AdminDashboard, meta: { requiresAuth: true, requiresAdmin: true } },
  { path: "/admin/aquariums", name: "Aquarium Management", component: AquariumManagement, meta: { requiresAuth: true, requiresAdmin: true } },
  { path: "/admin/users", name: "User Management", component: UserManagement, meta: { requiresAuth: true, requiresAdmin: true } },
  { path: "/admin/logs", name: "Log Management", component: LogManagement, meta: { requiresAuth: true, requiresAdmin: true } },

  // Blog Posts (Public)
  { path: "/blog/why-spreadsheets-fail-reef-keepers", name: "BlogWhySpreadsheetsFail", component: BlogWhySpreadsheetsFail },
  { path: "/blog/top-5-ways-to-keep-your-tank-stable", name: "BlogTop5WaysToKeepTankStable", component: BlogTop5WaysToKeepTankStable },
  { path: "/blog/best-reef-tank-log-app-free-and-paid-options-compared", name: "BlogBestReefTankLogAppOptions", component: BlogBestReefTankLogAppOptions },
  { path: "/blog/how-to-track-aquarium-parameters-without-expensive-controllers", name: "BlogHowToTrackParameters", component: BlogHowToTrackParameters },
  { path: "/blog/reef-tank-maintenance-schedule-template-free-download", name: "BlogReefTankMaintenanceSchedule", component: BlogReefTankMaintenanceSchedule },
  { path: "/blog/spreadsheets-vs-apps-the-best-way-to-record-aquarium-data", name: "BlogSpreadsheetsVsApps", component: BlogSpreadsheetsVsApps },
  { path: "/blog/how-sharing-your-reef-tank-logs-can-help-the-community", name: "BlogHowSharingLogsHelps", component: BlogHowSharingLogsHelps },
  { path: "/blog/beginners-guide-to-aquarium-parameter-logging", name: "BlogBeginnersGuideToLogging", component: BlogBeginnersGuideToLogging },
  { path: "/blog/top-10-most-important-reef-tank-parameters-to-monitor", name: "Top10MostImportant", component: BlogTop10MostImportant },
  // Catch-all route for 404 Not Found
  { path: "/:catchAll(.*)", name: "NotFound", component: NotFound },
];

// Create Vue Router instance
const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { top: 0 };
    }
  },
});

const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;

// Navigation Guard: Checks authentication & role
router.beforeEach(async (to, from) => {
  const authStore = useAuthStore();
  const popupStore = usePopupStore();
  const modalStore = useModalStore();
  modalStore.closeAllModals();

  async function fetchUser() {
    const response = await axios.get(`${apiBaseUrl}/me`, { withCredentials: true });
    authStore.setUser(response.data);
    return response;
  }

  try {
    await fetchUser(); // Try initial request

    // Auth check (this actually isn't needed since we check for auth in the fetchUser function but i still like it here)
    if (to.meta.requiresAuth && !authStore.user) {
      popupStore.setMessage("Authentication Error", "Authentication required");
      return { path: "/" };
    }

    // Admin check
    if (to.meta.requiresAdmin && authStore.user.role !== "admin") {
      popupStore.setMessage("Authentication Error", "Admin privileges required");
      return { path: "/" };
    }

    return true;
  } catch (error) {

    if (!to.meta.requiresAuth && !to.meta.requiresAdmin) {
      return true;
    }

    if (error.response?.status === 401) {
      const targetPath = to.fullPath.startsWith("/") ? to.fullPath : "/dashboard";
      const loginUrl = `${apiBaseUrl}/login?next=${encodeURIComponent(targetPath)}`;
      window.location.href = loginUrl;
      return false;
    } else {
      authStore.setUser(null);
      popupStore.setMessage("Authentication Error", error.response?.data?.message || "Unknown error");
      return { path: "/" };
    }
  }
});

export default router;
